import api from "api/axios";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { Agent, FileSystemItem } from "types";
import {
  AGENT_TYPES,
  AgentStatus,
  AgentTypeValue,
  AUTONOMOUS_AGENT_STATUS,
} from "constants/agents";
import { handleAxiosAndCaughtError } from "utils";

type AgentStatusValue = AgentStatus | AUTONOMOUS_AGENT_STATUS;

export interface PipelineItem {
  input: string;
  agent_id: string;
  output: string;
}

interface NewAgentContextType {
  formData: {
    name: string;
    description: string;
    icon: File | null;
    knowledgeBase: FileSystemItem[];
    workflows: string[];
    integration_ids: string[];
    agent_ids: string[];
    agent_type: AgentTypeValue | null;
    trigger_type: string | null;
    cron: string | null;
    pipelines: PipelineItem[];
  };
  setFormData: React.Dispatch<
    React.SetStateAction<{
      name: string;
      description: string;
      icon: File | null;
      knowledgeBase: FileSystemItem[];
      workflows: string[];
      integration_ids: string[];
      agent_ids: string[];
      agent_type: AgentTypeValue | null;
      trigger_type: string | null;
      cron: string | null;
      pipelines: PipelineItem[];
    }>
  >;
  handleSubmit: () => Promise<void>;
  isLoading: boolean;
  newAgentId: string | null;
  agentStatus: AgentStatusValue | null;
  isAgentReady: boolean;
  progress: number;
  stopPolling: () => void;
}

const NewAgentContext = createContext<NewAgentContextType | undefined>(
  undefined
);

export const NewAgentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: null as File | null,
    knowledgeBase: [] as FileSystemItem[],
    workflows: [],
    integration_ids: [],
    agent_ids: [],
    agent_type: null as AgentTypeValue | null,
    trigger_type: null as string | null,
    cron: null as string | null,
    pipelines: [] as PipelineItem[],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [newAgentId, setNewAgentId] = useState<null | string>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatusValue | null>(null);
  const [progress, setProgress] = useState(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Agent is ready if status is READY, or if it's an autonomous agent with IDLE or SCHEDULING status
  const isAgentReady =
    agentStatus === AgentStatus.READY ||
    agentStatus === AUTONOMOUS_AGENT_STATUS.IDLE ||
    agentStatus === AUTONOMOUS_AGENT_STATUS.SCHEDULING;

  // Poll agent status every 5s after creation
  useEffect(() => {
    if (newAgentId && !isAgentReady) {
      setAgentStatus(AgentStatus.TRAINING);
      pollingRef.current = setInterval(async () => {
        try {
          const res = await api.get<Agent>(`/agents/${newAgentId}`);
          setAgentStatus(res.data.status);
        } catch (err) {
          // Optionally handle error
        }
      }, 5000);
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
    return () => {};
  }, [newAgentId, isAgentReady]);

  // Stop polling when agent is ready
  useEffect(() => {
    if (isAgentReady && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [isAgentReady]);

  // Progress bar logic
  useEffect(() => {
    // Clear any existing progress interval first
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (isAgentReady) {
      setProgress(100);
      return;
    }
    if (agentStatus === AgentStatus.ERROR) {
      // Ensure the UI doesn't appear stuck at 99%
      setProgress(100);
      // Stop polling if currently active
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    if (agentStatus === AgentStatus.TRAINING) {
      let current = 0;
      const interval = setInterval(() => {
        current = Math.min(current + Math.random() * 2 + 1, 99);
        setProgress(current);
        if (current >= 99) {
          clearInterval(interval);
          progressIntervalRef.current = null;
        }
      }, 1000);
      progressIntervalRef.current = interval;
      return () => {
        clearInterval(interval);
        progressIntervalRef.current = null;
      };
    }
  }, [agentStatus, isAgentReady]);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Validate orchestrator requires assigned specialists
      if (
        formData.agent_type === AGENT_TYPES.ORCHESTRATOR &&
        (!formData.agent_ids || formData.agent_ids.length === 0)
      ) {
        toast.error(
          "At least one specialist is required for orchestrator agents"
        );
        setIsLoading(false);
        return;
      }

      const documents: Record<string, string> = {};
      formData.knowledgeBase.forEach((k) => {
        documents[k.path] = k.name;
      });

      const body = {
        name: formData.name,
        description: formData.description,
        icon_uri: "uri", // use the correct key
        documents, // assuming k.id is a valid UUID string
        workflow_ids: formData.workflows,
        integration_ids: formData.integration_ids,
        agent_ids: formData.agent_ids,
        // Include agent_type if it's set (now selected in Agent Details step for FUNCTIONAL)
        ...(formData.agent_type && { agent_type: formData.agent_type }),
        // Include cron expression if schedule trigger is selected
        ...{ cron: formData.cron ?? "" },
        // Include pipelines if they exist
        ...(formData.pipelines &&
          formData.pipelines.length > 0 && { pipelines: formData.pipelines }),
        schedule_type: formData.trigger_type,
      };

      const res = await api.post("/agents", body);

      // Check if the response indicates an error
      // Note: axios interceptor resolves HTTP errors instead of rejecting (returns error.response)
      // So we need to check res.status >= 400 to detect HTTP errors
      // Also check for undefined res (network errors) or missing id in response
      if (!res || (res.status && res.status >= 400) || !res.data?.id) {
        // Handle error case directly without throwing to avoid uncaught runtime errors
        // Clear any progress interval that might be running
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        // Reset state on error
        setNewAgentId(null);
        setAgentStatus(AgentStatus.ERROR);
        setProgress(100);
        // Stop any polling if it was started
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        // Error toast is already shown by axios interceptor for HTTP errors
        console.error(
          "Agent creation failed:",
          res?.data?.detail || res?.data?.message || "Failed to create agent"
        );
        return;
      }

      setNewAgentId(res.data.id);
      setAgentStatus(AgentStatus.TRAINING);
      setProgress(0);
      // Success toast is already shown by axios interceptor
    } catch (err: any) {
      // Catch block only executes for actual exceptions (not HTTP errors, which are resolved by interceptor)
      // This handles network errors, timeouts, or other unexpected exceptions
      // Clear any progress interval that might be running
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Reset state on error
      setNewAgentId(null);
      setAgentStatus(AgentStatus.ERROR);
      setProgress(100);
      // Stop any polling if it was started
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }

      // For network errors or other exceptions, show toast via handleAxiosAndCaughtError
      // HTTP errors are handled in the try block above (they resolve instead of rejecting)
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <NewAgentContext.Provider
      value={{
        formData,
        setFormData,
        handleSubmit,
        isLoading,
        newAgentId,
        agentStatus,
        isAgentReady,
        progress,
        stopPolling,
      }}
    >
      {children}
    </NewAgentContext.Provider>
  );
};

export const useNewAgent = () => {
  const context = useContext(NewAgentContext);
  if (context === undefined) {
    throw new Error("useNewAgent must be used within a NewAgentProvider");
  }
  return context;
};
